import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { removeRenderJob } from '../store/tracksSlice';
import { Play, Download, ArrowLeft, AlertTriangle, CheckCircle, Clock, Trash2, Sliders, X } from 'lucide-react';
import { apiFetch, apiPost, apiDelete, apiGet, API_BASE } from '../utils/api';
import { formatEta } from '../utils/formatters';
import { trackGAEvent } from '../utils/analytics';
import { connectSocket } from '../utils/socket';

interface RenderStatus {
  jobId: string;
  status: 'queued' | 'rendering' | 'completed' | 'failed';
  progress: number;
  startedAt?: string;
  elapsed?: number;
  etaSeconds?: number;
  smoothedEtaSeconds?: number;
  completionTime?: string;
  smoothedCompletionTime?: string;
  videoUrl?: string;
  error?: string;
  trackId?: string;
  resolution?: string;
  fps?: number;
  aspectRatio?: string;
  mapProvider?: string;
  hasPreview?: boolean;
}

const getMapProviderLabel = (provider?: string) => {
  const p = provider || 'google-satellite';
  switch (p) {
    case 'maptiler-satellite':
      return 'MapTiler Satellite';
    case 'google-satellite':
      return 'Google Satellite';
    case 'mapbox-satellite':
      return 'Mapbox Satellite';
    case 'openstreetmap':
      return 'OpenStreetMap';
    case 'cartodb-positron':
      return 'CartoDB Positron';
    case 'cartodb-darkmatter':
      return 'CartoDB Dark Matter';
    case 'cartodb-voyager':
      return 'CartoDB Voyager';
    default:
      return p;
  }
};

export const RenderStatusPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const token = useAppSelector((state) => state.auth.token);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [job, setJob] = useState<RenderStatus | null>(null);
  const [track, setTrack] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [cacheBust, setCacheBust] = useState<number>(Date.now());
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmType, setConfirmType] = useState<'forget' | 'cancel' | null>(null);
  const [sseActive, setSseActive] = useState(true);
  const [now, setNow] = useState<number>(Date.now());
  const lastSseTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!job || (job.status !== 'rendering' && job.status !== 'queued')) {
      return;
    }
    setNow(Date.now());
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [job?.status]);

  const cssAspectRatio = job?.aspectRatio ? job.aspectRatio.replace(':', '/') : '16/9';
  let previewMaxWidth = '100%';
  if (job?.aspectRatio === '9:16') {
    previewMaxWidth = '280px';
  } else if (job?.aspectRatio === '1:1') {
    previewMaxWidth = '400px';
  }

  useEffect(() => {
    if (job?.trackId && !track) {
      apiGet(`/api/tracks/${job.trackId}`)
        .then(setTrack)
        .catch((err) => console.error('Failed to load track info:', err));
    }
  }, [job?.trackId, track]);

  const handleRequeue = async () => {
    if (!job) return;
    setActionLoading(true);
    setError(null);
    try {
      let res;
      if (jobId) {
        try {
          res = await apiPost(`/api/render/${jobId}/retry`);
        } catch {
          // Fallback if retry endpoint is unreachable
        }
      }
      if (!res && job.trackId) {
        res = await apiPost(`/api/tracks/${job.trackId}/render`, {
          resolution: job.resolution || '1080p',
          fps: job.fps || 30,
          aspectRatio: job.aspectRatio || '16:9',
        });
      }
      if (res && res.jobId) {
        if (res.jobId === jobId) {
          // In-place retry: reset local job state and restart SSE progress connection
          setJob((prev) =>
            prev
              ? {
                  ...prev,
                  status: 'queued',
                  progress: 0,
                  error: undefined,
                  hasPreview: false,
                  smoothedEtaSeconds: undefined,
                  smoothedCompletionTime: undefined,
                }
              : null,
          );
          setSseActive(false);
          setTimeout(() => setSseActive(true), 50);
        } else {
          // New render job ID returned (fallback): navigate to the new status page
          navigate(`/status/${res.jobId}`);
          setSseActive(true);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to requeue render job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!jobId) return;
    setSseActive(false); // Cleanly stop SSE first
    setActionLoading(true);
    setError(null);
    try {
      if (confirmType === 'cancel') {
        // Cancel active render
        const updatedStatus = await apiPost(`/api/render/${jobId}/cancel`);
        setJob(updatedStatus);
        setActionLoading(false);
        setShowConfirmModal(false);
        setSseActive(true); // Restart SSE since job still exists
      } else {
        // Forget / Delete job
        await apiDelete(`/api/render/${jobId}`);
        dispatch(removeRenderJob(jobId));
        const toastMessage =
          job?.status === 'completed' ? 'Rendered video deleted successfully.' : 'Failed render job forgotten.';
        navigate('/', { state: { toastMessage } });
      }
    } catch (err: any) {
      setError(
        err.message || (confirmType === 'cancel' ? 'Failed to cancel render job' : 'Failed to delete render job'),
      );
      setActionLoading(false);
      setShowConfirmModal(false);
      if (confirmType === 'cancel') {
        setSseActive(true); // Restore SSE if cancellation failed
      }
    }
  };

  // Fetch initial status immediately on mount
  useEffect(() => {
    if (!jobId || !token) return;
    apiGet(`/api/render/${jobId}/status`)
      .then((data) => {
        if (data) {
          setJob((prev) => (prev ? { ...prev, ...data } : data));
        }
      })
      .catch((err) => console.error('Failed to fetch initial render status:', err));
  }, [jobId, token]);

  useEffect(() => {
    if (!jobId) return;
    const socket = connectSocket();
    if (!socket) return;

    const handleSocketEvent = (data: any) => {
      if (data && (data.jobId === jobId || data.id === jobId)) {
        lastSseTimeRef.current = Date.now();
        setJob((prevJob) => {
          if (!prevJob) return data;
          const validProgress =
            typeof data.progress === 'number' && data.progress >= 0 ? data.progress : prevJob.progress;
          const updated: RenderStatus = {
            ...prevJob,
            ...data,
            progress: validProgress,
            hasPreview: data.hasPreview || prevJob.hasPreview || false,
            startedAt: data.startedAt || prevJob.startedAt,
            elapsed: data.elapsed !== undefined ? data.elapsed : prevJob.elapsed,
            etaSeconds: data.etaSeconds !== undefined ? data.etaSeconds : prevJob.etaSeconds,
            completionTime: data.completionTime || prevJob.completionTime,
          };

          if (data.etaSeconds !== undefined) {
            const prevSmoothed = prevJob.smoothedEtaSeconds ?? data.etaSeconds;
            updated.smoothedEtaSeconds = Math.round(0.1 * data.etaSeconds + 0.9 * prevSmoothed);
            updated.smoothedCompletionTime = new Date(Date.now() + updated.smoothedEtaSeconds * 1000).toISOString();
          }

          return updated;
        });

        if (data.hasPreview) {
          setCacheBust(Date.now());
        }
      }
    };

    socket.on('render_preview', handleSocketEvent);
    socket.on('render_status', handleSocketEvent);

    return () => {
      socket.off('render_preview', handleSocketEvent);
      socket.off('render_status', handleSocketEvent);
    };
  }, [jobId]);

  useEffect(() => {
    if (!jobId || !token || !sseActive) return;

    let active = true;
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    const connectSSE = async () => {
      try {
        const response = await apiFetch(`/api/render/${jobId}/progress`);

        if (!response.ok) {
          throw new Error('Failed to subscribe to progress stream');
        }

        reader = response.body?.getReader() || null;
        if (!reader) {
          throw new Error('Progress stream reader unavailable');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (active) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.trim().startsWith('data:')) {
              const jsonStr = line.replace('data:', '').trim();
              try {
                const status = JSON.parse(jsonStr) as RenderStatus;
                if (active) {
                  lastSseTimeRef.current = Date.now();
                  setJob((prevJob) => {
                    if (!prevJob) {
                      if (status.etaSeconds !== undefined) {
                        status.smoothedEtaSeconds = status.etaSeconds;
                        status.smoothedCompletionTime = new Date(
                          Date.now() + status.etaSeconds * 1000,
                        ).toISOString();
                      }
                      return status;
                    }

                    const validProgress =
                      typeof status.progress === 'number' && status.progress >= 0 ? status.progress : prevJob.progress;

                    const updated: RenderStatus = {
                      ...prevJob,
                      ...status,
                      progress: validProgress,
                      hasPreview: status.hasPreview || prevJob.hasPreview || false,
                      startedAt: status.startedAt || prevJob.startedAt,
                      elapsed: status.elapsed !== undefined ? status.elapsed : prevJob.elapsed,
                      etaSeconds: status.etaSeconds !== undefined ? status.etaSeconds : prevJob.etaSeconds,
                      completionTime: status.completionTime || prevJob.completionTime,
                    };

                    if (status.etaSeconds !== undefined) {
                      const prevSmoothed = prevJob.smoothedEtaSeconds ?? status.etaSeconds;
                      // Exponential Moving Average: 10% new value, 90% old value for high stability
                      updated.smoothedEtaSeconds = Math.round(0.1 * status.etaSeconds + 0.9 * prevSmoothed);
                      updated.smoothedCompletionTime = new Date(
                        Date.now() + updated.smoothedEtaSeconds * 1000,
                      ).toISOString();
                    }

                    return updated;
                  });

                  if (status.status === 'completed' || status.status === 'failed') {
                    active = false;
                  }
                }
              } catch (err) {
                console.warn('Failed to parse SSE data', err);
              }
            }
          }
        }
      } catch (err: any) {
        if (active) {
          setError(err.message || 'Connection lost');
        }
      }
    };

    connectSSE();

    return () => {
      active = false;
      if (reader) {
        reader.cancel().catch(() => {});
      }
    };
  }, [jobId, token, sseActive]);

  const getDisplayElapsed = (): number | undefined => {
    if (!job) return undefined;
    if (job.status === 'completed' || job.status === 'failed') {
      return job.elapsed;
    }
    if (job.startedAt) {
      const startMs = new Date(job.startedAt).getTime();
      if (!isNaN(startMs)) {
        return Math.max(0, Math.floor((now - startMs) / 1000));
      }
    }
    if (job.elapsed !== undefined) {
      return Math.max(0, job.elapsed + Math.floor((now - lastSseTimeRef.current) / 1000));
    }
    return job.elapsed;
  };

  const getDisplayEta = (): number | undefined => {
    if (!job || job.status !== 'rendering') return undefined;

    const completionIso = job.smoothedCompletionTime ?? job.completionTime;
    if (completionIso) {
      const completionMs = new Date(completionIso).getTime();
      if (!isNaN(completionMs)) {
        return Math.max(0, Math.round((completionMs - now) / 1000));
      }
    }

    const etaBase = job.smoothedEtaSeconds ?? job.etaSeconds;
    if (etaBase !== undefined) {
      const elapsedSinceSse = Math.floor((now - lastSseTimeRef.current) / 1000);
      return Math.max(0, etaBase - elapsedSinceSse);
    }

    return job.smoothedEtaSeconds ?? job.etaSeconds;
  };

  const displayElapsed = getDisplayElapsed();
  const displayEta = getDisplayEta();

  const formatTime = (seconds?: number) => {
    if (seconds === undefined || seconds < 0) return '--:--';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const getStatusText = (status?: string, error?: string | null) => {
    if (status === 'failed' && error === 'Render job cancelled by user') {
      return 'Render Cancelled';
    }
    switch (status) {
      case 'queued':
        return 'In Queue';
      case 'rendering':
        return 'Rendering 3D frames';
      case 'completed':
        return 'Video Completed';
      case 'failed':
        return 'Render Failed';
      default:
        return 'Loading status...';
    }
  };

  // SVG Ring calculation
  const radius = 90;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - ((job?.progress || 0) / 100) * circumference;

  const playerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showPlayer && playerRef.current) {
      playerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [showPlayer]);

  return (
    <div
      style={{
        padding: 'clamp(16px, 4vw, 40px) 16px',
        maxWidth: '680px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Top Navigation Bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/library')}
          className="btn btn-secondary"
          style={{ padding: '8px 12px', minHeight: '40px', borderRadius: '10px' }}
          title="Back to Library"
        >
          <ArrowLeft size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            style={{
              fontSize: 'clamp(20px, 4vw, 26px)',
              fontWeight: 800,
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Render Job Details
          </h1>
        </div>
      </div>

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '14px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
            color: 'var(--status-error)',
            marginBottom: '24px',
            fontSize: '14px',
          }}
        >
          <AlertTriangle size={20} style={{ flexShrink: 0 }} />
          <span>Error: {error}</span>
        </div>
      )}

      {job ? (
        <div className="glass-panel" style={{ padding: 'clamp(20px, 4vw, 36px)', textAlign: 'center' }}>
          {/* Status Header Badge / Progress Ring */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px', position: 'relative' }}>
            {job.status === 'completed' ? (
              <div
                style={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '2px solid var(--status-success)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--status-success)',
                  boxShadow: '0 0 30px rgba(16, 185, 129, 0.15)',
                }}
              >
                <CheckCircle size={60} />
              </div>
            ) : job.status === 'failed' && job.error === 'Render job cancelled by user' ? (
              <div
                style={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '50%',
                  background: 'rgba(245, 158, 11, 0.08)',
                  border: '2px solid #f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#f59e0b',
                  boxShadow: '0 0 30px rgba(245, 158, 11, 0.15)',
                }}
              >
                <AlertTriangle size={60} />
              </div>
            ) : job.status === 'failed' ? (
              <div
                style={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '50%',
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '2px solid var(--status-error)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--status-error)',
                  boxShadow: '0 0 30px rgba(239, 68, 68, 0.15)',
                }}
              >
                <AlertTriangle size={60} />
              </div>
            ) : (
              <div style={{ position: 'relative', width: '180px', height: '180px' }}>
                <svg width="180" height="180" viewBox="0 0 200 200" style={{ transform: 'rotate(-90deg)' }}>
                  <circle
                    cx="100"
                    cy="100"
                    r={radius}
                    fill="transparent"
                    stroke="rgba(255,255,255,0.04)"
                    strokeWidth="8"
                  />
                  <circle
                    cx="100"
                    cy="100"
                    r={radius}
                    fill="transparent"
                    stroke="url(#progress-grad)"
                    strokeWidth="8"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                  />
                  <defs>
                    <linearGradient id="progress-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ff385c" />
                      <stop offset="100%" stopColor="#ff7a00" />
                    </linearGradient>
                  </defs>
                </svg>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                  <span style={{ fontSize: '32px', fontWeight: 800, color: '#fff', display: 'block' }}>
                    {job.progress}%
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      color: 'var(--text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 600,
                    }}
                  >
                    {job.status === 'queued' ? 'queued' : 'rendering'}
                  </span>
                </div>
              </div>
            )}
          </div>

          <h2 style={{ fontSize: 'clamp(20px, 3.5vw, 24px)', marginBottom: '6px', color: '#fff', fontWeight: 700 }}>
            {getStatusText(job.status, job.error)}
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '24px', wordBreak: 'break-all' }}>
            Job Reference ID:{' '}
            <code
              style={{
                fontSize: '12px',
                background: 'rgba(0,0,0,0.3)',
                padding: '3px 8px',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.05)',
                color: 'var(--accent-secondary)',
              }}
            >
              {job.jobId}
            </code>
          </p>

          {/* Hero Media Section (Video Player / Live Preview / Poster) */}
          {job.status === 'completed' && showPlayer && (
            <div
              ref={playerRef}
              style={{
                marginBottom: '28px',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid var(--border-glass)',
                boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
                background: '#000',
                maxWidth: previewMaxWidth,
                margin: '0 auto 28px',
                position: 'relative',
              }}
            >
              <video
                controls
                src={`${API_BASE}/api/render/${jobId}/download?token=${token}&stream=true`}
                style={{ width: '100%', display: 'block', borderRadius: '16px', aspectRatio: cssAspectRatio }}
                autoPlay
              />
            </div>
          )}

          {job.status === 'completed' && !showPlayer && job.hasPreview && (
            <div style={{ marginBottom: '28px', textAlign: 'center' }}>
              <div
                onClick={() => setShowPlayer(true)}
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: previewMaxWidth,
                  aspectRatio: cssAspectRatio,
                  borderRadius: '14px',
                  overflow: 'hidden',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid var(--border-glass)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  margin: '0 auto',
                  cursor: 'pointer',
                }}
              >
                <img
                  src={`${API_BASE}/api/render/${jobId}/preview?token=${token}&t=${cacheBust}`}
                  alt="Rendered Video Poster"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s ease',
                  }}
                >
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: 'rgba(255, 56, 92, 0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      boxShadow: '0 6px 20px rgba(255, 56, 92, 0.5)',
                      paddingLeft: '4px',
                    }}
                  >
                    <Play size={28} style={{ fill: '#fff' }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Live Preview Image (when rendering) */}
          {job.status === 'rendering' && (
            <div style={{ marginBottom: '28px', textAlign: 'center' }}>
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: previewMaxWidth,
                  aspectRatio: cssAspectRatio,
                  borderRadius: '14px',
                  overflow: 'hidden',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid var(--border-glass)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  margin: '0 auto',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    background: 'rgba(11, 15, 25, 0.75)',
                    backdropFilter: 'blur(4px)',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    zIndex: 2,
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: 'var(--accent-primary)',
                      boxShadow: '0 0 8px var(--accent-primary)',
                      display: 'inline-block',
                      animation: 'pulse 1.5s infinite',
                    }}
                  />
                  <span style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}>Live Preview</span>
                  <span
                    style={{
                      color: 'var(--accent-secondary)',
                      paddingLeft: '4px',
                      borderLeft: '1px solid rgba(255,255,255,0.15)',
                    }}
                  >
                    {job.progress}%
                  </span>
                </div>

                {job.hasPreview ? (
                  <img
                    src={`${API_BASE}/api/render/${jobId}/preview?token=${token}&t=${cacheBust}`}
                    alt="Last Rendered Frame"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="preview-placeholder"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--text-secondary)',
                      fontSize: '14px',
                      gap: '12px',
                    }}
                  >
                    <div className="spinner" style={{ width: '24px', height: '24px', borderWidth: '2px' }} />
                    <span>Awaiting initial live preview frame...</span>
                  </div>
                )}

                {/* Bottom Edge Render Progress Bar */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '5px',
                    background: 'rgba(0, 0, 0, 0.6)',
                    zIndex: 4,
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, Math.max(0, job.progress || 0))}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #ff385c, #ff7a00)',
                      boxShadow: '0 0 10px rgba(255, 56, 92, 0.8)',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Render Stats Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '12px',
              padding: '16px 20px',
              background: 'rgba(0,0,0,0.18)',
              borderRadius: '12px',
              border: '1px solid var(--border-glass)',
              marginBottom: '24px',
              textAlign: 'left',
            }}
          >
            <div>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  display: 'block',
                  textTransform: 'uppercase',
                  marginBottom: '4px',
                  fontWeight: 600,
                }}
              >
                Elapsed Time
              </span>
              <strong style={{ fontSize: '17px', color: '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={16} style={{ color: 'var(--text-secondary)' }} />
                <span>{formatTime(displayElapsed)}</span>
              </strong>
            </div>

            {job.status === 'rendering' && (
              <div>
                <span
                  style={{
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                    display: 'block',
                    textTransform: 'uppercase',
                    marginBottom: '4px',
                    fontWeight: 600,
                  }}
                >
                  ETA
                </span>
                <strong style={{ fontSize: '17px', color: 'var(--accent-secondary)' }}>
                  {formatEta(displayEta)}
                </strong>
              </div>
            )}

            {(job.smoothedCompletionTime || job.completionTime) && (
              <div style={{ gridColumn: 'span 2' }}>
                <span
                  style={{
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                    display: 'block',
                    textTransform: 'uppercase',
                    marginBottom: '4px',
                    fontWeight: 600,
                  }}
                >
                  {job.status === 'completed' ? 'Finished At' : 'Estimated Finish'}
                </span>
                <strong style={{ fontSize: '14px', color: '#fff' }}>
                  {new Date(job.smoothedCompletionTime ?? job.completionTime!).toLocaleString()}
                </strong>
              </div>
            )}
          </div>

          {job.status === 'failed' && job.error && (
            <div
              style={{
                padding: '14px 16px',
                background:
                  job.error === 'Render job cancelled by user' ? 'rgba(245, 158, 11, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                border:
                  job.error === 'Render job cancelled by user'
                    ? '1px solid rgba(245, 158, 11, 0.15)'
                    : '1px solid rgba(239, 68, 68, 0.1)',
                borderRadius: '10px',
                color: job.error === 'Render job cancelled by user' ? '#f59e0b' : 'var(--status-error)',
                marginBottom: '24px',
                fontSize: '14px',
                textAlign: 'left',
              }}
            >
              <strong>
                {job.error === 'Render job cancelled by user' ? 'Cancellation Message:' : 'Error Message:'}
              </strong>{' '}
              {job.error}
            </div>
          )}

          {/* Action Buttons Bar */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '28px',
              width: '100%',
            }}
          >
            {job.status === 'completed' && (
              <>
                <a
                  href={`${API_BASE}/api/render/${jobId}/download?token=${token}`}
                  download
                  onClick={() =>
                    trackGAEvent('download_video', {
                      resolution: job?.resolution,
                      job_id: jobId,
                      track_id: job?.trackId,
                    })
                  }
                  className="btn btn-primary"
                  style={{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 700,
                    minHeight: '44px',
                    flex: '1 1 180px',
                    boxSizing: 'border-box',
                  }}
                >
                  <Download size={18} />
                  <span>Download MP4</span>
                </a>

                <button
                  onClick={() => setShowPlayer(!showPlayer)}
                  className="btn btn-secondary"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    minHeight: '44px',
                    flex: '1 1 160px',
                    boxSizing: 'border-box',
                  }}
                >
                  <Play size={18} />
                  <span>{showPlayer ? 'Close Player' : 'Play Video'}</span>
                </button>

                <button
                  onClick={() => {
                    setConfirmType('forget');
                    setShowConfirmModal(true);
                  }}
                  className="btn btn-danger"
                  disabled={actionLoading}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    minHeight: '44px',
                    flex: '1 1 140px',
                    boxSizing: 'border-box',
                  }}
                >
                  <Trash2 size={18} />
                  <span>Delete Video</span>
                </button>
              </>
            )}

            {(job.status === 'queued' || job.status === 'rendering') && (
              <button
                onClick={() => {
                  setConfirmType('cancel');
                  setShowConfirmModal(true);
                }}
                className="btn btn-danger"
                disabled={actionLoading}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 600,
                  minHeight: '44px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <X size={18} />
                <span>Cancel Render</span>
              </button>
            )}

            {job.status === 'failed' && (
              <>
                <button
                  onClick={handleRequeue}
                  className="btn btn-primary"
                  disabled={actionLoading}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 700,
                    minHeight: '44px',
                    flex: '1 1 160px',
                    boxSizing: 'border-box',
                  }}
                >
                  <Play size={18} />
                  <span>Requeue Video</span>
                </button>
                <button
                  onClick={() => {
                    setConfirmType('forget');
                    setShowConfirmModal(true);
                  }}
                  className="btn btn-danger"
                  disabled={actionLoading}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    minHeight: '44px',
                    flex: '1 1 140px',
                    boxSizing: 'border-box',
                  }}
                >
                  <Trash2 size={18} />
                  <span>Forget Job</span>
                </button>
              </>
            )}

            {job.trackId && (
              <button
                onClick={() =>
                  navigate(
                    `/preview/${job.trackId}?resolution=${job.resolution}&fps=${job.fps}&aspectRatio=${job.aspectRatio}`,
                  )
                }
                className="btn btn-secondary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 600,
                  minHeight: '44px',
                  flex: '1 1 180px',
                  boxSizing: 'border-box',
                }}
                disabled={actionLoading}
              >
                <Sliders size={18} />
                <span>Customize in Preview</span>
              </button>
            )}
          </div>

          {/* Activity & Video Details Panel */}
          {track && (
            <div
              style={{
                textAlign: 'left',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: '12px',
                padding: '20px',
              }}
            >
              <h3
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  marginBottom: '14px',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  paddingBottom: '8px',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Activity & Video Details
              </h3>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '16px 12px',
                  fontSize: '13px',
                }}
              >
                <div>
                  <span
                    style={{
                      fontSize: '11px',
                      color: 'var(--text-muted)',
                      display: 'block',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      marginBottom: '2px',
                    }}
                  >
                    Activity Name
                  </span>
                  <span
                    style={{
                      color: '#fff',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {track.name}
                  </span>
                </div>
                <div>
                  <span
                    style={{
                      fontSize: '11px',
                      color: 'var(--text-muted)',
                      display: 'block',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      marginBottom: '2px',
                    }}
                  >
                    Type
                  </span>
                  <span style={{ textTransform: 'capitalize', color: '#fff', fontWeight: 600 }}>
                    {track.activityType === 'running'
                      ? '🏃 Running'
                      : track.activityType === 'cycling'
                        ? '🚴 Cycling'
                        : track.activityType === 'hiking'
                          ? '🥾 Hiking'
                          : track.activityType === 'climbing'
                            ? '🧗 Climbing'
                            : '📦 Other'}
                  </span>
                </div>
                {(track.startTime || track.createdAt) && (
                  <div>
                    <span
                      style={{
                        fontSize: '11px',
                        color: 'var(--text-muted)',
                        display: 'block',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        marginBottom: '2px',
                      }}
                    >
                      Activity Date
                    </span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>
                      {new Date(track.startTime || track.createdAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                )}
                <div>
                  <span
                    style={{
                      fontSize: '11px',
                      color: 'var(--text-muted)',
                      display: 'block',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      marginBottom: '2px',
                    }}
                  >
                    Distance & Duration
                  </span>
                  <span style={{ color: '#fff', fontWeight: 600 }}>
                    {(track.totalDistance / 1000).toFixed(1)} km ({Math.floor(track.totalDuration / 60)}m)
                  </span>
                </div>
                <div>
                  <span
                    style={{
                      fontSize: '11px',
                      color: 'var(--text-muted)',
                      display: 'block',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      marginBottom: '2px',
                    }}
                  >
                    Resolution & Aspect Ratio
                  </span>
                  <span style={{ color: '#fff', fontWeight: 600 }}>
                    {job.resolution} ({job.aspectRatio || '16:9'} @ {job.fps}fps)
                  </span>
                </div>
                <div>
                  <span
                    style={{
                      fontSize: '11px',
                      color: 'var(--text-muted)',
                      display: 'block',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      marginBottom: '2px',
                    }}
                  >
                    Map Provider
                  </span>
                  <span style={{ color: '#fff', fontWeight: 600 }}>
                    {getMapProviderLabel(job.mapProvider || track.mapProvider)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="glass-panel" style={{ padding: '80px 40px', textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 20px' }} />
          <p style={{ color: 'var(--text-secondary)' }}>Connecting to render monitor...</p>
        </div>
      )}

      {/* Custom Confirmation Modal */}
      {showConfirmModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(8px)',
            padding: '16px',
            boxSizing: 'border-box',
          }}
        >
          <div
            className="glass-panel"
            style={{
              width: '100%',
              maxWidth: '440px',
              background: 'rgba(15, 23, 42, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'center', color: 'var(--status-error)', marginBottom: '16px' }}
            >
              <AlertTriangle size={48} />
            </div>

            <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#fff', marginBottom: '8px' }}>
              {confirmType === 'cancel'
                ? 'Cancel this render job?'
                : job?.status === 'completed'
                  ? 'Delete this rendered video?'
                  : 'Forget this render job?'}
            </h3>

            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.5, marginBottom: '24px' }}>
              {confirmType === 'cancel'
                ? 'Are you sure you want to cancel this active render? This will immediately terminate the rendering process and discard any generated frames. This action cannot be undone.'
                : job?.status === 'completed'
                  ? 'Are you sure you want to delete this rendered video file? This will permanently remove the video from storage and delete the job record. This action cannot be undone.'
                  : 'Are you sure you want to forget this failed render? This will permanently delete the job logs and clean up any files. This action cannot be undone.'}
            </p>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={handleDeleteConfirm}
                className="btn btn-danger"
                style={{ flex: 1, minWidth: '130px', padding: '12px 16px', fontSize: '14px', borderRadius: '10px' }}
                disabled={actionLoading}
              >
                {actionLoading
                  ? confirmType === 'cancel'
                    ? 'Cancelling...'
                    : 'Deleting...'
                  : confirmType === 'cancel'
                    ? 'Yes, Cancel Render'
                    : job?.status === 'completed'
                      ? 'Yes, Delete Video'
                      : 'Yes, Forget It'}
              </button>

              <button
                onClick={() => setShowConfirmModal(false)}
                className="btn btn-secondary"
                style={{ flex: 1, minWidth: '130px', padding: '12px 16px', fontSize: '14px', borderRadius: '10px' }}
                disabled={actionLoading}
              >
                {confirmType === 'cancel' ? 'Keep Rendering' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
